import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { Organization } from "../types";
import { useLocale } from "../../../context/LanguageContext";
import { MutationErrorBanner } from "../../../components/ui/MutationErrorBanner";

interface SuspendConfirmDialogProps {
  organization: Organization;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading: boolean;
  error?: string | null;
}

export function SuspendConfirmDialog({
  organization,
  onConfirm,
  onCancel,
  loading,
  error,
}: SuspendConfirmDialogProps) {
  const { t } = useLocale();
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");

  // i18n: localised confirmation word (e.g. SUSPEND / SPERREN) so German admins
  // aren't asked to type an English word in an otherwise-German UI.
  const requiredConfirmWord = t("adminUI.organizations.suspendConfirmWord");
  const isConfirmed = confirmText === requiredConfirmWord;
  const isReasonValid = reason.trim().length >= 10;
  const canSubmit = isConfirmed && isReasonValid && !loading;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onCancel} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-destructive/10 p-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">
              {t("organizations.suspend.title")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("organizations.suspend.intro", { name: organization.name })}
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <span className="text-destructive">•</span>
                {t("organizations.suspend.bullet1")}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-destructive">•</span>
                {t("organizations.suspend.bullet2")}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-destructive">•</span>
                {t("organizations.suspend.bullet3")}
              </li>
            </ul>
          </div>
        </div>

        {/* Reason */}
        <div className="mt-4">
          <label
            htmlFor="suspend-reason"
            className="block text-sm font-medium text-foreground"
          >
            {t("organizations.suspend.reasonLabel")}
          </label>
          <textarea
            id="suspend-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("adminUI.organizations.suspendReasonPlaceholder")}
            rows={3}
            className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {reason.length > 0 && !isReasonValid && (
            <p className="mt-1 text-xs text-destructive">
              {t("organizations.validation.reasonLength")}
            </p>
          )}
        </div>

        {/* High-friction confirm */}
        <div className="mt-4">
          <label
            htmlFor="suspend-confirm"
            className="block text-sm font-medium text-foreground"
          >
            {t("organizations.suspend.typePrefix")}{" "}
            <span className="rounded bg-destructive/10 px-1.5 py-0.5 font-mono text-destructive">
              {requiredConfirmWord}
            </span>{" "}
            {t("organizations.suspend.typeSuffix")}
          </label>
          <input
            id="suspend-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={requiredConfirmWord}
            className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            autoComplete="off"
          />
        </div>

        {error && (
          <div className="mt-4">
            <MutationErrorBanner message={error} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={() => canSubmit && onConfirm(reason.trim())}
            disabled={!canSubmit}
            className="h-9 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t("organizations.suspend.submitting") : t("organizations.suspend.title")}
          </button>
        </div>
      </div>
    </>
  );
}
